import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { SEO, SITE_NAME } from "@/data/seo";
import { CONTACT, SOCIALS, RELEASES } from "@/data/content";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const setTag = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => k !== "tag" && el.setAttribute(k, v));
  el.setAttribute("data-seo", "1");
  return el;
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const personLd = (lang) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${ORIGIN}/#person`,
  name: CONTACT.name,
  jobTitle: lang === "de" ? "Saxophonistin" : "Saxophonist",
  description: SEO[""][lang].description,
  url: `${ORIGIN}/${lang}`,
  image: `${ORIGIN}/images/bio-portrait.webp`,
  telephone: "+491795840220",
  email: CONTACT.email,
  address: { "@type": "PostalAddress", streetAddress: CONTACT.street, postalCode: "47839", addressLocality: "Krefeld", addressRegion: "Nordrhein-Westfalen", addressCountry: "DE" },
  sameAs: SOCIALS.map((s) => s.url),
  knowsAbout: ["Saxophone", "Jazz", "Neo Soul", "Live Music", "Event Music"],
});

const groupLd = (lang, name, id, genre, albumIds) => ({
  "@context": "https://schema.org",
  "@type": "MusicGroup",
  "@id": `${ORIGIN}/#${id}`,
  name,
  genre,
  url: `${ORIGIN}/${lang}/bands/${id}`,
  founder: { "@id": `${ORIGIN}/#person` },
  areaServed: ["Krefeld", "Düsseldorf", "Köln", "Nordrhein-Westfalen", "Deutschland"],
  album: RELEASES.filter((r) => albumIds.includes(r.id)).map((r) => ({ "@type": "MusicAlbum", name: r.title, datePublished: r.year, image: `${ORIGIN}${r.cover}`, byArtist: { "@id": `${ORIGIN}/#person` } })),
});

const NAME_MAP = { biography: { de: "Biografie", en: "Biography" }, bands: { de: "Bands", en: "Bands" }, quartett: { de: "Quartett", en: "Quartet" }, "full-band": { de: "Band", en: "Band" }, events: { de: "Music for Events", en: "Music for Events" }, music: { de: "Music", en: "Music" }, discography: { de: "Diskografie", en: "Discography" }, video: { de: "Videos", en: "Videos" }, equipment: { de: "Equipment", en: "Equipment" }, press: { de: "Presse", en: "Press" }, contact: { de: "Kontakt", en: "Contact" }, lessons: { de: "Sax-Coaching", en: "Sax-Coaching" }, impressum: { de: "Impressum", en: "Imprint" }, datenschutz: { de: "Datenschutz", en: "Privacy" } };

export const Seo = ({ notFound = false }) => {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const parts = pathname.split("/").filter(Boolean);
    const route = parts.slice(1).join("/");
    const meta = notFound ? SEO.notfound[lang] : (SEO[route] || SEO[""])[lang];
    const canonical = `${ORIGIN}/${lang}${route ? `/${route}` : ""}`;

    document.documentElement.lang = lang;
    document.title = meta.title;
    setTag('meta[name="description"]', { name: "description", content: meta.description });
    setTag('meta[name="robots"]', { name: "robots", content: notFound ? "noindex, follow" : "index, follow, max-image-preview:large" });
    setTag('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });
    ["de", "en"].forEach((l) =>
      setTag(`link[rel="alternate"][hreflang="${l}"]`, { tag: "link", rel: "alternate", hreflang: l, href: `${ORIGIN}/${l}${route ? `/${route}` : ""}` })
    );
    setTag('link[rel="alternate"][hreflang="x-default"]', { tag: "link", rel: "alternate", hreflang: "x-default", href: `${ORIGIN}/de${route ? `/${route}` : ""}` });

    const og = { "og:type": "website", "og:site_name": SITE_NAME, "og:locale": lang === "de" ? "de_DE" : "en_US", "og:title": meta.title, "og:description": meta.description, "og:url": canonical, "og:image": `${ORIGIN}/images/hero.webp`, "og:image:width": "1536", "og:image:height": "1024" };
    Object.entries(og).forEach(([k, v]) => setTag(`meta[property="${k}"]`, { property: k, content: v }));
    const tw = { "twitter:card": "summary_large_image", "twitter:title": meta.title, "twitter:description": meta.description, "twitter:image": `${ORIGIN}/images/hero.webp` };
    Object.entries(tw).forEach(([k, v]) => setTag(`meta[name="${k}"]`, { name: k, content: v }));

    setJsonLd("ld-person", personLd(lang));
    setJsonLd("ld-website", { "@context": "https://schema.org", "@type": "WebSite", name: SITE_NAME, url: `${ORIGIN}/${lang}`, inLanguage: lang, publisher: { "@id": `${ORIGIN}/#person` } });
    if (route.startsWith("bands")) {
      setJsonLd("ld-band", groupLd(lang, "Angela Puxi Band", "full-band", ["Neo Soul", "Funk", "Nu-Jazz"], ["badapapapaa", "badapapapaa-reloaded"]));
      setJsonLd("ld-quartett", groupLd(lang, lang === "de" ? "Angela Puxi Quartett" : "Angela Puxi Quartet", "quartett", ["Jazz"], ["isle-of-fire"]));
    } else {
      document.getElementById("ld-band")?.remove();
      document.getElementById("ld-quartett")?.remove();
    }
    if (route) {
      const items = [{ "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/${lang}` }];
      parts.slice(1).forEach((seg, i) => {
        items.push({ "@type": "ListItem", position: i + 2, name: NAME_MAP[seg]?.[lang] || seg, item: `${ORIGIN}/${lang}/${parts.slice(1, i + 2).join("/")}` });
      });
      setJsonLd("ld-breadcrumb", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items });
    } else {
      document.getElementById("ld-breadcrumb")?.remove();
    }
  }, [pathname, lang, notFound]);

  return null;
};
